import { SortDirection } from '../enums/SortDirection';

type IFieldSelector<Item> = (item: Item) => (typeof item)[keyof typeof item];

type IReturnType<Item> = (a: Item, b: Item) => SortDirection;

/*
    sortBy util provides possibility to sort each property with different sort direction.

    params:
    fieldSelectors: array of fields to sorted
    sortingDirections: array of sorting directions

    examples:
    // sort by one field
    [].sort(
        sortBy(
            (item) => item.name,
            SortDirection.DESC,
        ),
    );

    // or using default ASC sorting
    [].sort(
        sortBy((item) => item.name),
    );

    // Sorting multiple fields
    [].sort(
        [(item) => item.name, (item) => item.score],
        [SortDirection.DESC, SortDirection.ASC]
    )
*/

const sortBy = <Item>(
    fieldSelectors: IFieldSelector<Item> | IFieldSelector<Item>[],
    sortingDirections: SortDirection | SortDirection[] = SortDirection.ASC,
): IReturnType<Item> => {
    const fields: IFieldSelector<Item>[] = !Array.isArray(fieldSelectors)
        ? [fieldSelectors]
        : fieldSelectors;
    const directions: SortDirection[] = !Array.isArray(sortingDirections)
        ? Array<SortDirection>(fields.length).fill(sortingDirections)
        : sortingDirections;

    return (a, b): SortDirection => {
        for (let i = 0; i < fields.length; i++) {
            if (fields[i](a) < fields[i](b)) {
                return directions[i];
            }
            if (fields[i](a) > fields[i](b)) {
                return -directions[i];
            }
        }
        return 0;
    };
};

export default sortBy;
